import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfoIcon } from '../../../icons';
import { HeartLoading } from '../../../HeartLoading';
import { removeQueryString } from '../../../../hooks/useQueryString';
import {
    ApiHandler,
    ErrorResponse,
    GetGiftByUUIDResponse,
} from '../../../../api';
import { Button, Modal } from '../../../ui';

const extractDate = (date?: string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

export type EnvelopeWithAmount = {
    amount: number;
    envelopeId: number;
};

type RetryDonationProps = {
    retryDonationId: string | undefined;
    handleFailedDonation: (
        envelopes: EnvelopeWithAmount[],
        callback: () => unknown,
    ) => unknown;
    setRetryDonationId: React.Dispatch<
        React.SetStateAction<string | undefined>
    >;
};

const initialRequestState = {
    loading: false,
    error: null as ErrorResponse | null,
    data: null as GetGiftByUUIDResponse | null,
};

export const retryDonationQueryString = 'retryDonationId';
export const RetryDonation: React.FC<RetryDonationProps> = ({
    retryDonationId,
    setRetryDonationId,
    handleFailedDonation,
}) => {
    const [requestState, setRequestState] = useState(initialRequestState);

    const handleClose = useCallback(() => {
        setRequestState(initialRequestState);
        setRetryDonationId(undefined);
        removeQueryString(retryDonationQueryString);
    }, [setRetryDonationId]);

    const makeFetchRequest = useCallback(
        async (giftId: string) => {
            setRequestState({
                loading: true,
                error: null,
                data: null,
            });
            const request =
                await ApiHandler.instance.donor.getGiftByUUID(giftId);
            if (request.success) {
                const payload = request.data.response.payload;

                if (!payload) {
                    handleClose();
                } else {
                    setRequestState({
                        loading: false,
                        error: null,
                        data: request.data,
                    });
                }
            } else {
                setRequestState({
                    loading: false,
                    error: request.error,
                    data: null,
                });
                handleClose();
            }
        },
        [handleClose],
    );

    useEffect(() => {
        if (!retryDonationId) return;
        makeFetchRequest(retryDonationId);
    }, [retryDonationId, makeFetchRequest]);

    const handleFailedDonationClick = () => {
        const envelopes = requestState.data?.response.payload.envelopes;
        const e: EnvelopeWithAmount[] | undefined = envelopes?.map((x) => ({
            amount: x.amount,
            envelopeId: x.id,
        }));
        e && handleFailedDonation(e, handleClose);
    };

    if (!retryDonationId) return null;
    const data = requestState.data?.response.payload;
    const isLoading =
        requestState.loading || (!requestState.data && !requestState.error);
    return (
        <Modal
            open={true}
            onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
        >
            <div className="flex flex-col gap-4 align-middle justify-center w-[412px] mobile:w-full">
                {isLoading ? (
                    <HeartLoading />
                ) : (
                    <div className="flex flex-col gap-6">
                        <div className="flex align-middle justify-center mx-auto">
                            <ErrorInfoIcon />
                        </div>
                        <div className="text-heading-small text-center">
                            Your recent gift
                            <br />
                            didn’t go through
                        </div>
                        <div className="flex flex-row gap-1 p-4 rounded-3xl justify-between align-middle border-utility-separator border border-solid">
                            <div className="text-left">
                                <div className="text-body-medium">
                                    {data?.donee.name || 'Organization'}
                                </div>
                                <div>
                                    <span className="text-body-small text-utility-error text-left">
                                        Failed
                                    </span>{' '}
                                    <span className="text-body-small text-primary text-left">
                                        - {extractDate(data?.gift.date)}
                                    </span>
                                </div>
                            </div>
                            <span className="text-center text-body-medium-bold my-auto">
                                ${data?.gift.amount}
                            </span>
                        </div>
                        <Button
                            onClick={handleFailedDonationClick}
                            className="w-full"
                        >
                            Try Gift Again
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="plain-secondary"
                        >
                            Dismiss
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};
